import React, { useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Marquee from "./effects/Marquee";
import MagnetButton from "./effects/MagnetButton";
import StreamText from "./effects/StreamText";

interface ContactSectionProps {
  t: Record<string, string>;
  title: string;
  subtitle: string;
  telegram: string;
  chatButton: string;
  active: boolean;
}

const ease = [0.2, 0.8, 0.2, 1] as const;
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } },
  exit: { transition: { staggerChildren: 0.04, staggerDirection: -1 } },
};
const fadeSlideUp = {
  hidden: { opacity: 0, y: 22 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease } },
  exit: { opacity: 0, y: -10, transition: { duration: 0.25 } },
};
const contactCardReveal = {
  hidden: { opacity: 0, y: 28, scale: 0.96 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.7, ease, delay: 0.25 } },
  exit: { opacity: 0, y: -12, scale: 0.97, transition: { duration: 0.3 } },
};
const quickAction = {
  hidden: { opacity: 0, y: 14 },
  visible: (i: number) => ({
    opacity: 1, y: 0,
    transition: { delay: 0.45 + i * 0.1, duration: 0.5, ease },
  }),
  exit: { opacity: 0, y: -8, transition: { duration: 0.2 } },
};
const marqueeReveal = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { delay: 0.6, duration: 0.8 } },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};

export const ContactSection: React.FC<ContactSectionProps> = ({ t, title, subtitle, telegram, chatButton, active }) => {
  const telegramLink = `https://t.me/${telegram.replace("@", "")}`;
  const streamKeyRef = useRef(0);
  if (active) streamKeyRef.current++;

  const handleChat = () => {
    // Abre el bot directamente para que el usuario pueda escribir
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.openTelegramLink(telegramLink);
    } else {
      window.open(telegramLink, "_blank", "noopener,noreferrer");
    }
  };

  const triggerCommand = (cmd: string) => {
    const command = cmd.replace('/', '');
    window.dispatchEvent(new CustomEvent('telegram-command', { detail: { command } }));
  };

  return (
    <div className="absolute inset-0" style={{ color: 'var(--c-text)', fontFamily: 'var(--ff-body)' }}>
      <AnimatePresence mode="wait">
        {active && (
          <motion.div
            key="contact-content"
            variants={stagger}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="absolute inset-0"
          >
            {/* Title area */}
            <motion.div
              variants={fadeSlideUp}
              style={{ position: 'absolute', top: 'var(--safe-top, 62px)', left: 18, right: 18, paddingTop: 'var(--sp-5)' }}
            >
              <div style={{
                fontFamily: 'var(--ff-body)', fontSize: 'var(--fs-micro)',
                letterSpacing: '0.22em', textTransform: 'uppercase',
                color: 'var(--c-crimson)', fontWeight: 600,
              }}>
                <StreamText key={`co-eb-${streamKeyRef.current}`} text={t.contact_tag || "Contacto"} speed={40} delay={100} caret={false} />
              </div>
              <h2 style={{
                fontFamily: 'var(--ff-display)', fontSize: 'var(--fs-hero)',
                margin: '10px 0 0 0', lineHeight: 0.86, textTransform: 'uppercase',
              }}>
                {t.contact_header_1 || "Escríbeme"}<br/>
                <span style={{
                  fontFamily: 'var(--ff-italic)', fontStyle: 'italic',
                  fontSize: 'clamp(42px, 7vw, 56px)', color: 'var(--c-crimson)',
                  letterSpacing: '-0.01em', textTransform: 'none',
                }}>{title}.</span>
              </h2>
              <p style={{
                marginTop: 20, fontSize: 'var(--fs-body)', lineHeight: 1.55,
                color: 'var(--c-text-dim)', maxWidth: 280,
              }}>
                {subtitle || (t.contact_subtitle || "Sin filtros, sin intermediarios. Telegram es el canal — respondo personal.")}
              </p>
            </motion.div>

            {/* Contact card */}
            <motion.div
              variants={contactCardReveal}
              whileHover={{ y: -2, transition: { duration: 0.2 } }}
              style={{
                position: 'absolute', top: 'calc(var(--safe-top, 62px) + 290px)',
                left: 18, right: 18,
                border: '1px solid var(--c-line)', background: 'var(--c-surface)',
                backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                padding: 20,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.5, ease }}
                  style={{
                    width: 48, height: 48, borderRadius: '50%', background: 'var(--c-crimson)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--c-on-crimson)',
                    boxShadow: '0 0 0 4px rgb(var(--c-crimson-rgb) / 0.15)',
                  }}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path d="M22 2L11 13M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
                  </svg>
                </motion.div>
                <div>
                  <div style={{ fontSize: 'var(--fs-micro)', letterSpacing: '0.2em', color: 'var(--c-muted)', textTransform: 'uppercase' }}>{t.contact_platform || "Telegram"}</div>
                  <div style={{ fontFamily: 'var(--ff-display)', fontSize: 22, letterSpacing: '0.02em' }}>{telegram}</div>
                </div>
              </div>

              <div style={{
                marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--c-line)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <div>
                  <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--c-text-dim)' }}>{t.contact_response_label || "Suele responder en"}</div>
                  <div style={{ fontFamily: 'var(--ff-display)', fontSize: 18, color: 'var(--c-crimson)', letterSpacing: '0.03em', marginTop: 2 }}>
                    {t.contact_response_time || "~ 12 MIN"}
                  </div>
                </div>
                <MagnetButton
                  onClick={handleChat}
                  className="glow-button"
                  style={{
                    background: 'var(--c-crimson)', color: 'var(--c-on-crimson)', border: 'none',
                    padding: '13px 18px', fontSize: 11,
                    letterSpacing: '0.18em', textTransform: 'uppercase' as const,
                    fontWeight: 700, cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                  }}
                >
                  {chatButton || "Abrir chat"} <span>→</span>
                </MagnetButton>
              </div>
            </motion.div>

            {/* Quick Actions */}
            <div style={{
              position: 'absolute', top: 'calc(var(--safe-top, 62px) + 460px)',
              left: 18, right: 18,
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10,
            }}>
              {[
                ['/sub', t.contact_action_sub || 'Suscribirse'],
                ['/vip', t.contact_action_vip || 'VIP plus']
              ].map(([k, v], i) => (
                <motion.button
                  key={k}
                  variants={quickAction}
                  custom={i}
                  className="hover-lift"
                  whileTap={{ scale: 0.96 }}
                  onClick={() => triggerCommand(k)}
                  style={{
                    border: '1px solid var(--c-line)', background: 'var(--c-surface)',
                    backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                    padding: '14px 14px', cursor: 'pointer', color: 'var(--c-text)',
                    textAlign: 'left', borderRadius: 'calc(2px * var(--radius-scale, 1))',
                  }}
                >
                  <div style={{ fontFamily: 'var(--ff-display)', fontSize: 16, letterSpacing: '0.04em', color: 'var(--c-crimson)' }}>{k}</div>
                  <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--c-text-dim)', marginTop: 4 }}>{v}</div>
                </motion.button>
              ))}
            </div>

            {/* Live marquee */}
            <motion.div
              variants={marqueeReveal}
              style={{
                position: 'absolute', bottom: 116, left: 0, right: 0,
                padding: '10px 0', borderTop: '1px solid var(--c-line)', borderBottom: '1px solid var(--c-line)',
                fontFamily: 'var(--ff-display)', fontSize: 14, letterSpacing: '0.06em',
                color: 'var(--c-text-dim)',
              }}
            >
              <Marquee items={[
                t.marquee_item_1 || 'NUEVO DROP CADA VIERNES',
                t.marquee_item_2 || 'RESPUESTA EN MENOS DE 15 MIN',
                t.marquee_item_3 || 'CANAL VIP · ACCESO INMEDIATO',
                t.marquee_item_4 || 'PAGO SEGURO POR BRE-B / PAYPAL',
              ]} speed={26} />
            </motion.div>

            {/* Bottom Meta */}
            <motion.div variants={fadeSlideUp} style={{ position: 'absolute', bottom: 56, left: 18 }}>
              <div style={{
                display: 'flex', alignItems: 'baseline', gap: 10,
                fontFamily: 'var(--ff-body)', color: 'var(--c-text-dim)',
                fontSize: 'var(--fs-caption)', letterSpacing: '0.18em', textTransform: 'uppercase',
              }}>
                <span style={{ fontFamily: 'var(--ff-display)', fontSize: 22, color: 'var(--c-text)', letterSpacing: '0.02em', lineHeight: 1 }}>05</span>
                <span style={{ color: 'var(--c-muted)' }}>/ 05</span>
                <span style={{ width: 24, height: 1, background: 'var(--c-line)', margin: '0 6px', display: 'inline-block' }}></span>
                <span>{t.contact_footer || "Contacto"}</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContactSection;
